import Hls from "hls.js";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { unstable_createElement } from "react-native-web";
import { AudioPlaybackHandle, AudioPlaybackSurfaceProps } from "./audioPlaybackTypes";

export const AudioPlaybackSurface = forwardRef<
  AudioPlaybackHandle,
  AudioPlaybackSurfaceProps
>(function AudioPlaybackSurface(
  { uri, headers, paused, volume, onReady, onProgress, onEnded, onError },
  ref,
) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onErrorRef = useRef(onError);
  const pausedRef = useRef(paused);
  onErrorRef.current = onError;
  pausedRef.current = paused;
  const headerSignature = JSON.stringify(headers ?? {});
  useImperativeHandle(ref, () => ({
    seek: (seconds) => {
      if (audioRef.current) audioRef.current.currentTime = Math.max(0, seconds);
    },
  }));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !uri) return;
    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: false,
        lowLatencyMode: false,
        xhrSetup: (request, requestUrl) => {
          const correctedUrl = requestUrl.includes("/universal")
            ? requestUrl
            : requestUrl.replace(/\/Audio\/([^/]+)\//, "/Videos/$1/");
          request.open("GET", correctedUrl, true);
          for (const [key, value] of Object.entries(
            JSON.parse(headerSignature) as Record<string, string>,
          )) request.setRequestHeader(key, value);
        },
      });
      hls.attachMedia(audio);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls?.loadSource(uri));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!pausedRef.current) void audio.play().catch(() => undefined);
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        hls?.destroy();
        hls = null;
        audio.src = uri;
        audio.load();
        if (!pausedRef.current) void audio.play().catch(() => undefined);
      });
    } else {
      audio.src = uri;
      audio.load();
    }
    return () => {
      hls?.destroy();
      audio.removeAttribute("src");
      audio.load();
    };
  }, [headerSignature, uri]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = Number.isFinite(volume)
      ? Math.max(0, Math.min(1, volume))
      : 1;
    if (paused) audio.pause();
    else void audio.play().catch(() => undefined);
  }, [paused, uri, volume]);

  const reportDuration = () => {
    const nextDuration = audioRef.current?.duration;
    onReady(Number.isFinite(nextDuration) && (nextDuration ?? 0) > 0 ? nextDuration ?? 0 : 0);
  };

  return unstable_createElement("audio", {
    ref: (node: HTMLAudioElement | null) => {
      audioRef.current = node;
    },
    onLoadedMetadata: reportDuration,
    onDurationChange: reportDuration,
    onTimeUpdate: () => {
      if (audioRef.current) onProgress(audioRef.current.currentTime);
    },
    onEnded,
    onError: () => onErrorRef.current("The browser could not play this track."),
  });
});
